import {
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
} from "react-share";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { CommonClassNameAndTestId } from "@typings/Common";
import { cn, getFormattedParameters } from "@util/helpers";

import { WHATSAPP_URL } from "./utils/constants";
import { ButtonSize, SocialMediaType } from "./utils/types";

export type SocialEmbedButtonFactoryProps = {
  /** The type of social media to render */
  whichSocialMedia: SocialMediaType;
  /** The URL to share */
  shareUrl: string;
  /** Common classes for all the button */
  commonCss: string;
  /** Article title for social sharing */
  title?: string;
  /** Icon image size */
  iconSize?: number;
  /** Button size - small, medium, large */
  buttonSize?: ButtonSize;
  /** Text in the button normally is beside the icon  */
  text?: string;
  /** Extra callback to call on click */
  callback?: () => void;
} & CommonClassNameAndTestId;

export default function SocialEmbedButtonFactory({
  whichSocialMedia = SocialMediaType.facebook,
  shareUrl = "",
  className = "",
  commonCss,
  title,
  iconSize = 24,
  buttonSize,
  text,
  callback,
}: SocialEmbedButtonFactoryProps) {
  const parametersList = [
    { key: "subject", value: "Sharing from The Business Times" },
    { key: "body", value: `${title} - ${shareUrl}` },
  ];

  const parameters = getFormattedParameters(parametersList);

  switch (whichSocialMedia) {
    case SocialMediaType.twitter:
      return (
        <TwitterShareButton
          url={shareUrl}
          className={cn(
            "text-gray-850",
            { "aspect-square": !text },
            commonCss,
            className
          )}
          title={title}
          data-size={buttonSize}
          resetButtonStyle={false}
          data-testid="share-button-twitter"
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.twitter}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </TwitterShareButton>
      );

    case SocialMediaType.telegram:
      return (
        <TelegramShareButton
          url={shareUrl}
          className={cn({ "aspect-square": !text }, commonCss, className)}
          title={title}
          data-size={buttonSize}
          resetButtonStyle={false}
          data-testid="share-button-telegram"
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.telegram}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </TelegramShareButton>
      );

    case SocialMediaType.whatsapp:
      return (
        <a
          className={cn({ "aspect-square": !text }, commonCss, className)}
          href={`${WHATSAPP_URL}${encodeURIComponent(title || "")} ${encodeURIComponent(shareUrl || "")}`}
          target="_blank"
          rel="noreferrer"
          data-size={buttonSize}
          data-testid="share-button-whatsapp"
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.whatsapp}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </a>
      );

    case SocialMediaType.email:
      return (
        <a
          href={`mailto: + ${parameters}`}
          data-testid="share-button-email"
          className={cn(
            "block",
            { "aspect-square": !text },
            commonCss,
            className
          )}
          target="_blank"
          rel="noreferrer"
          data-size={buttonSize}
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.email}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </a>
      );

    case SocialMediaType.linkedin:
      return (
        <LinkedinShareButton
          url={shareUrl}
          className={cn({ "aspect-square": !text }, commonCss, className)}
          title={title}
          data-size={buttonSize}
          resetButtonStyle={false}
          data-testid="share-button-linkedin"
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.linkedin}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </LinkedinShareButton>
      );

    case SocialMediaType.facebook:
    default:
      return (
        <FacebookShareButton
          url={shareUrl}
          className={cn({ "aspect-square": !text }, commonCss, className)}
          title={title}
          data-size={buttonSize}
          resetButtonStyle={false}
          data-testid="share-button-facebook"
          onClick={callback}
        >
          <IconFactory
            type={IconTypeEnum.facebook}
            width={iconSize}
            height={iconSize}
          />

          {text ? <p>{text}</p> : null}
        </FacebookShareButton>
      );
  }
}
